<script setup lang="ts">
// SRV › Attachments (docs/STORAGE.md §2.6): whether attachments are kept, and in which of the
// server's own folders and S3 buckets, with how much space they may use there. By default
// attachments aren't backed up: the server's own copy is only held until delivered. Server
// storage holds attachments only (HRTH-183): libraries live in their owners' buckets and
// nodes, which they add in Settings › Your storage (an admin running hrth on the server's own
// machine adds it as a node there).
import { computed, onMounted, ref } from 'vue'
import { describeError } from '../api/http'
import type { StorageLocation, StorageOverview } from '../api/types'
import { useSession } from '../stores/session'
import StorageAddForm from './StorageAddForm.vue'
import AttachmentLocationModal from './AttachmentLocationModal.vue'
import { gbText, mbFromGb, whereIs } from '../lib/memberStorage'
import { formatBytes } from '../lib/pattern'

const session = useSession()
const overview = ref<StorageOverview | null>(null)
const busy = ref(false)
const adding = ref(false)
/** The server's own locations: the only ones attachments can go in. */
const serverLocations = computed(() => overview.value?.locations.filter((l) => !l.owner) ?? [])
/** The location being edited. */
const ruling = ref<StorageLocation | null>(null)
/** Space for attachments in the data directory, in GB as typed. */
const dataDirGb = ref('')
/** Whether new attachments go to a location, and their space there, in a few words. */
function useOf(l: StorageLocation): string {
  const mb = l.policy?.attachments_mb
  if (overview.value?.attachments !== l.id) return 'not used for new attachments'
  return `new attachments${mb != null ? ` (${gbText(mb)} GB)` : ''}`
}

const where = whereIs

async function load(): Promise<void> {
  try {
    overview.value = await session.api.storage()
    dataDirGb.value = gbText(overview.value.data_dir?.attachments_mb)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function run(fn: () => Promise<void>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

async function added(l: StorageLocation): Promise<void> {
  adding.value = false
  await load()
  if (l.notice) session.toast(l.notice, 'error')
  else session.toast(l.kind === 's3' ? 'Bucket added and set up for browsers (CORS)' : 'Location added')
  // This page's CSP predates the bucket: reload so it can upload and stream there (HRTH-125).
  if (l.kind === 's3') setTimeout(() => location.reload(), 1500)
}

const check = (l: StorageLocation) => run(() => session.api.checkStorage(l.id), `${l.name} works`)

const remove = (l: StorageLocation) =>
  run(async () => {
    if (!confirm(`Remove ${l.name}? Files already there are left where they are.`)) return
    await session.api.removeStorage(l.id)
    await load()
  })

const saveDataDir = () =>
  run(async () => {
    const mb = mbFromGb(dataDirGb.value)
    if (mb === undefined) throw new Error('Sizes are numbers of GB, or blank for no limit')
    await session.api.setDataDirPolicy({ attachments_mb: mb })
    await load()
  }, 'Saved')

const setAttachments = (id: string) =>
  run(async () => {
    await session.api.setAttachmentStorage(id || null)
    await load()
  }, id ? 'New attachments will be kept there' : 'Attachments are not backed up')

onMounted(load)
</script>

<template>
  <section v-if="overview" class="border-b border-border px-6 py-5" data-testid="storage-settings">
    <h2 class="label">Attachments · {{ String(serverLocations.length).padStart(2, '0') }}</h2>
    <p class="mono mb-3 text-text-muted">
      Folders and S3 buckets of this server's for keeping attachments. They're encrypted on people's devices first, so
      neither this server nor the storage can read them. Libraries aren't kept here: people keep them in their own
      buckets and nodes (Settings › Your storage); to share folders on this machine, run
      <code class="select-all">hrth node setup</code> in them.
    </p>

    <div class="mono grid grid-cols-[1fr_2fr_5rem_6rem] gap-3 border-b border-border-strong pb-1.5 text-text-muted uppercase max-sm:grid-cols-[1fr_5rem]">
      <span>Location</span>
      <span class="max-sm:hidden">Where</span>
      <span class="text-right">Files</span>
      <span class="max-sm:hidden" />
    </div>
    <p v-if="!serverLocations.length" class="mono border-b border-border py-2 text-text-muted" data-testid="storage-none">
      No storage added yet.
    </p>
    <div
      v-for="l in serverLocations"
      :key="l.id"
      class="grid grid-cols-[1fr_2fr_5rem_6rem] items-center gap-3 border-b border-border py-2 text-sm max-sm:grid-cols-[1fr_5rem]"
      :data-testid="`storage-${l.name}`"
    >
      <span class="min-w-0 leading-tight">
        <span class="block truncate">{{ l.name }}</span>
        <span class="mono block text-text-muted uppercase">{{ l.kind === 's3' ? 'S3' : 'Folder' }}{{ l.configured ? ' · configured' : '' }}</span>
        <span class="mono block truncate text-text-muted" :data-testid="`storage-use-${l.name}`">{{ useOf(l) }}</span>
      </span>
      <span class="mono truncate text-text-muted max-sm:hidden" :title="where(l)">{{ where(l) }}</span>
      <span class="mono text-right text-text-muted">{{ l.attachments }}</span>
      <span class="flex justify-end gap-1 max-sm:col-span-2">
        <button type="button" class="btn-ghost px-2" :disabled="busy" :data-testid="`storage-rules-${l.name}`" @click="ruling = l">Edit</button>
        <button type="button" class="btn-ghost px-2 max-sm:hidden" :disabled="busy" data-testid="storage-check" @click="check(l)">Check</button>
        <button
          v-if="!l.configured"
          type="button"
          class="btn-ghost px-2 hover:text-signal max-sm:hidden"
          :disabled="busy"
          :aria-label="`Remove ${l.name}`"
          data-testid="storage-remove"
          @click="remove(l)"
        >
          ✕
        </button>
      </span>
    </div>

    <h3 class="label mt-5">Where new attachments go</h3>
    <select
      v-if="serverLocations.length"
      id="attachment-location"
      class="input mb-1 max-w-80"
      :value="overview.attachments ?? ''"
      :disabled="busy || overview.attachments_configured"
      aria-label="Where new attachments are kept"
      data-testid="attachment-location"
      @change="setAttachments(($event.target as HTMLSelectElement).value)"
    >
      <option value="">Not backed up</option>
      <option v-for="l in serverLocations" :key="l.id" :value="l.id">Kept in {{ l.name }}</option>
    </select>
    <p class="mono text-text-muted" data-testid="attachment-backup">
      <template v-if="!overview.attachments">
        Attachments are not backed up. This server holds each one only until everyone's devices have downloaded it, 30
        days at most.<template v-if="serverLocations.length"> Choose a location above to keep them.</template>
        <template v-else> Add storage below to keep them.</template>
      </template>
      <template v-else>New attachments are kept in {{ overview.locations.find((l) => l.id === overview!.attachments)?.name }}. Ones already sent stay where they are.</template>
      <template v-if="overview.attachments_configured"> Set in the server's configuration.</template>
    </p>
    <form v-if="!overview.attachments" class="mt-2 flex flex-wrap items-center gap-2" data-testid="data-dir-space" @submit.prevent="saveDataDir">
      <label class="mono text-text-muted" for="data-dir-gb">Space for attachments here</label>
      <input id="data-dir-gb" v-model="dataDirGb" inputmode="decimal" class="input w-24" placeholder="no limit" data-testid="data-dir-gb" />
      <span class="mono text-text-muted">GB · {{ formatBytes(overview.data_dir?.attachment_bytes ?? 0) }} used</span>
      <button class="btn-ghost" :disabled="busy" data-testid="data-dir-save">Save</button>
    </form>
    <p v-else class="mono mt-1 text-text-muted">Their space there is set under Edit.</p>

    <button v-if="!adding" type="button" class="btn-ghost mt-4 px-2" data-testid="storage-add-open" @click="adding = true">+ Add location</button>
    <StorageAddForm
      v-else
      :submit="(req) => session.api.addStorage(req)"
      :disk-roots="overview.disk_roots"
      @added="added"
      @cancel="adding = false"
    />
    <AttachmentLocationModal v-if="ruling" :location="ruling" @close="ruling = null" @saved="load().then(() => (ruling = overview?.locations.find((l) => l.id === ruling?.id) ?? null))" />
  </section>
</template>
